/**
 * RangeDownloads (KINDW-104 / FSD PLP-06)
 *
 * Renders the optional download CTAs ("Download product card", "Download assembly manual")
 * for a Range Detail PLP. Each CTA is gated by the corresponding Contentful asset on the
 * `Product Listing - Dynamic` entry — when an asset is missing the button is omitted and
 * the layout closes the gap (per FSD PLP-06 §4).
 *
 * Scope:
 *   - Downloads only. The "Configure Your Own" CTA (PLP-06 §3) is not rendered here because
 *     its target URL is TBC and it lives in the sticky nav bar (separate ticket).
 *
 * Inputs are the raw Contentful Asset shape returned by `BaseProductListingFragment`
 * (`{url, title, description, contentType}`). Any falsy or missing `url` skips that CTA.
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {Button, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RangeDownloadIcon} from '@amblique/king-living/app/components/icons';

const ContentfulAssetShape = PropTypes.shape({
  url: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  contentType: PropTypes.string,
});

const hasDownloadableAsset = (asset) => Boolean(asset?.url);

/**
 * Build a sensible filename for the `download` attribute. We prefer the asset title
 * (Contentful's "Title") falling back to the URL's basename. The browser will append the
 * correct extension based on `Content-Type` returned by Contentful's CDN, but providing a
 * download attribute keeps the filename deterministic across browsers.
 */
const getSuggestedFilename = (asset) => {
  if (!asset) return undefined;
  if (asset.title) return asset.title;
  try {
    const {pathname} = new URL(asset.url);
    return decodeURIComponent(pathname.split('/').pop() || '') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * @param {object} props
 * @param {{url?: string, title?: string, description?: string, contentType?: string}} [props.productCard]
 *   Contentful asset for the "Download product card" CTA. Renders nothing when `url` is missing.
 * @param {{url?: string, title?: string, description?: string, contentType?: string}} [props.assemblyManual]
 *   Contentful asset for the "Download assembly manual" CTA. Renders nothing when `url` is missing.
 */
export const RangeDownloads = ({productCard, assemblyManual}) => {
  const styles = useMultiStyleConfig('RangeDownloads', {});
  const showProductCard = hasDownloadableAsset(productCard);
  const showAssemblyManual = hasDownloadableAsset(assemblyManual);

  if (!showProductCard && !showAssemblyManual) {
    return null;
  }

  return (
    <Flex data-testid="range-downloads" {...styles.container}>
      {showProductCard && (
        <Button
          as="a"
          variant="range-download-filled"
          size="range-download"
          leftIcon={<RangeDownloadIcon boxSize="18px" />}
          href={productCard.url}
          download={getSuggestedFilename(productCard)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="range-downloads-product-card"
          aria-label={productCard.description || productCard.title || undefined}
          justifyContent="center"
          width={{base: 'full', md: 'auto'}}
        >
          <FormattedMessage
            defaultMessage="Download Product card"
            id="range_downloads.cta.product_card"
          />
        </Button>
      )}

      {showAssemblyManual && (
        <Button
          as="a"
          variant="range-download-outlined"
          size="range-download"
          leftIcon={<RangeDownloadIcon boxSize="18px" />}
          href={assemblyManual.url}
          download={getSuggestedFilename(assemblyManual)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="range-downloads-assembly-manual"
          aria-label={assemblyManual.description || assemblyManual.title || undefined}
          justifyContent="center"
          bg="brandColors.primaryBG"
          width={{base: 'full', md: 'auto'}}
        >
          <FormattedMessage
            defaultMessage="Download Assembly manual"
            id="range_downloads.cta.assembly_manual"
          />
        </Button>
      )}
    </Flex>
  );
};

RangeDownloads.displayName = 'RangeDownloads';
RangeDownloads.propTypes = {
  productCard: ContentfulAssetShape,
  assemblyManual: ContentfulAssetShape,
};

export default RangeDownloads;
